/**
 * Check a set of suggested places against Google Places.
 *
 * Talisman Explorer and the scheduler's "pick a spot" options come from a
 * model writing place names and addresses from memory. Older cached suggestions
 * may also carry model-estimated ratings. Most places are real; some are on the
 * wrong street or have never existed.
 *
 * The rating was the tell: the model's own schema called it an estimate, and
 * the card showed it as though it were Google's. So the same lookup that
 * fetches the real rating is the one that decides whether the place is there
 * at all.
 *
 * A text search almost always returns SOMETHING for a query carrying a city
 * name, so "found nothing" alone is a weak filter. The real test is that the
 * name that came back is recognisably the name we asked for (namesMatch).
 */

import { loadGoogleMaps } from './googleMapsLoader';
import { namesMatch } from '../utils/placeNames';

export interface PlaceFacts {
  /** Google's own formatted address, which replaces the model's. */
  address?: string;
  rating?: number;
  ratingTotal?: number;
}

/**
 * Returns the confirmed places keyed by the name they were asked for. A name
 * absent from the map could not be confirmed and should not be offered.
 *
 * Returns **null** when Places itself is unavailable — no key, the library
 * failed to load. That is not a verdict on the suggestions, so callers show
 * them unchanged rather than emptying the list because a key is missing.
 *
 * Lookups run in parallel: eight of them sequentially is a visible wait, and
 * the caller is holding its loading state while this runs.
 */
export async function confirmPlaces(
  asked: { name?: string; address?: string }[],
): Promise<Map<string, PlaceFacts> | null> {
  if (!asked.length) return new Map();
  try {
    await loadGoogleMaps();
  } catch {
    return null;
  }
  const g = (window as any).google;
  if (!g?.maps?.places) return null;

  const svc = new g.maps.places.PlacesService(document.createElement('div'));
  const lookup = (query: string) => new Promise<any>(resolve => {
    svc.findPlaceFromQuery(
      { query, fields: ['name', 'formatted_address', 'rating', 'user_ratings_total'] },
      (results: any, status: any) => resolve(
        status === g.maps.places.PlacesServiceStatus.OK && results?.[0] ? results[0] : null,
      ),
    );
  });

  const confirmed = new Map<string, PlaceFacts>();
  await Promise.all(asked.map(async item => {
    const name = (item.name || '').trim();
    if (!name) return;
    const found = await lookup(item.address ? `${name}, ${item.address}` : name);
    if (!found || !namesMatch(name, found.name)) return;
    confirmed.set(name, {
      address: found.formatted_address || item.address,
      // A real place with no rating shows none, rather than keeping the
      // estimate it arrived with.
      ...(found.rating != null ? { rating: found.rating, ratingTotal: found.user_ratings_total } : {}),
    });
  }));
  return confirmed;
}
